import * as React from 'react';
import {RangeType} from "rsuite/DateRangePicker";
import moment from "moment/moment";
import {DateRangePicker} from "rsuite";

export type DateRanges = 'Today' | 'Yesterday' | '2 Days Ago' | 'Last 7 Days' | 'Month to Date' | 'Month to Yesterday' | 'Last 30 Days' | 'Last Month' | '7 to 45 Days' | 'Last 60 Days' | 'Last 6 Month' | 'Last 12 Month' | 'Last 24 Month' | 'Year to Date'

const RANGE_BUILDERS: Record<string, () => [Date, Date]> = {
    'Today': () => [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
    'Yesterday': () => [moment().subtract(1, "day").startOf("day").toDate(), moment().subtract(1, "day").endOf("day").toDate()],
    '2 Days Ago': () => [moment().subtract(2, "day").startOf("day").toDate(), moment().subtract(2, "day").endOf("day").toDate()],
    'Last 7 Days': () => [moment().subtract(7, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
    'Month to Date': () => [moment().startOf("month").toDate(), moment().endOf("day").toDate()],
    'Month to Yesterday': () => [moment().startOf("month").toDate(), moment().subtract(1, "day").endOf("day").toDate()],
    'Last 30 Days': () => [moment().subtract(30, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
    'Last Month': () => [moment().subtract(1, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
    '7 to 45 Days': () => [moment().subtract(45, "days").startOf("day").toDate(), moment().endOf("day").subtract(8, "days").toDate()],
    'Last 60 Days': () => [moment().subtract(60, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
    'Last 6 Month': () => [moment().subtract(6, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
    'Last 12 Month': () => [moment().subtract(12, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
    'Last 24 Month': () => [moment().subtract(24, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
    'Year to Date': () => [moment().startOf("year").toDate(), moment().endOf("day").toDate()],
};

const rangePlacement = () => (window.innerWidth < 768 ? 'bottom' : 'left') as 'bottom' | 'left';

export const buildPredefinedRanges = (): RangeType[] =>
    Object.keys(RANGE_BUILDERS).map((label) => ({
        label,
        value: RANGE_BUILDERS[label](),
        placement: rangePlacement(),
    }));

const predefinedRanges: RangeType[] = buildPredefinedRanges();

/** Copy Y/M/D from `day` onto the time-of-day from `timeSource`. */
const withTimeFrom = (day: Date, timeSource: Date) =>
    moment(day)
        .hour(timeSource.getHours())
        .minute(timeSource.getMinutes())
        .second(timeSource.getSeconds())
        .millisecond(0)
        .toDate();

/**
 * If `clicked` is outside a complete range, move only that endpoint.
 * Returns null when the click is inside (or on) the range — caller should keep default selection.
 */
export const endpointAwareAdjust = (clicked: Date, range: [Date, Date]): [Date, Date] | null => {
    const [start, end] = range;
    if (!start || !end) return null;

    const clickedDay = moment(clicked).startOf("day");
    const startDay = moment(start).startOf("day");
    const endDay = moment(end).startOf("day");

    if (clickedDay.isBefore(startDay)) {
        return [withTimeFrom(clicked, start), end];
    }
    if (clickedDay.isAfter(endDay)) {
        return [start, withTimeFrom(clicked, end)];
    }
    return null;
};

const normalizeRange = (value: [Date, Date]): [Date, Date] => {
    const next: [Date, Date] = [new Date(value[0].valueOf()), new Date(value[1].valueOf())];
    if (next[1].getHours() === 0 && next[1].getMinutes() === 0 && next[1].getSeconds() === 0) {
        next[1].setHours(23, 59, 59);
    }
    return next;
};

export default function DateRange({showTime = true, dateRange, setDateRange, style, placement}:{showTime?: boolean, dateRange: any, setDateRange: any, style: any, placement?: 'bottomStart' | 'bottomEnd'}) {
    let j = 0;
    const ranges = React.useMemo(() => buildPredefinedRanges(), []);
    const pickerRef = React.useRef<{ open?: () => void; close?: () => void } | null>(null);
    const [pickerKey, setPickerKey] = React.useState(0);
    const [calendarDefault, setCalendarDefault] = React.useState<[Date, Date] | undefined>();
    const reopenAfterRemountRef = React.useRef(false);
    // True after the first click of a fresh in-range selection (waiting for end date).
    const midSelectRef = React.useRef(false);

    const commitRange = React.useCallback((value: [Date, Date]) => {
        setDateRange(normalizeRange(value));
    }, [setDateRange]);

    React.useEffect(() => {
        if (!reopenAfterRemountRef.current) return;
        reopenAfterRemountRef.current = false;
        queueMicrotask(() => pickerRef.current?.open?.());
    }, [pickerKey]);

    const handleSelect = React.useCallback((date: Date) => {
        if (midSelectRef.current) {
            midSelectRef.current = false;
            return;
        }

        if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
            const adjusted = endpointAwareAdjust(date, dateRange as [Date, Date]);
            if (adjusted) {
                const normalized = normalizeRange(adjusted);
                const isStartAdjust = moment(date).startOf("day").isBefore(moment(dateRange[0]).startOf("day"));

                commitRange(normalized);
                midSelectRef.current = false;

                if (isStartAdjust) {
                    // rsuite resets the right calendar to start+1 month on a lone start click;
                    // remount with both endpoints so the end month panel stays put, then reopen.
                    setCalendarDefault(normalized);
                    reopenAfterRemountRef.current = true;
                    setPickerKey((k) => k + 1);
                } else {
                    queueMicrotask(() => pickerRef.current?.close?.());
                }
                return;
            }
        }

        midSelectRef.current = true;
    }, [commitRange, dateRange]);

    return (
            <DateRangePicker
                key={pickerKey}
                ref={pickerRef as any}
                format={showTime ? "MM-dd-yyyy HH:mm:ss" : "MM-dd-yyyy"}
                value={dateRange}
                defaultCalendarValue={calendarDefault}
                showOneCalendar={window.innerWidth < 768}
                ranges={ranges}
                onSelect={handleSelect}
                onChange={(value: any) => {
                    midSelectRef.current = false;
                    if (value?.[1]?.getHours?.() === 0) {
                        value[1].setHours(23, 59, 59);
                    }
                    setDateRange(value);
                }}
                cleanable={false}
                preventOverflow={false}
                style={style}
                placement={window.innerWidth < 768 ? 'bottomStart' : (placement || 'bottomStart')}
                onFocus={(e: any) => {j++; if (j === 1) { e.target.blur(); }}}
                onExit={() => {
                    j = 0;
                    midSelectRef.current = false;
                    setCalendarDefault(undefined);
                }}
            />
    )
}

export const RangeToTimes = (defaultRange: DateRanges) => {
    return RANGE_BUILDERS[defaultRange]?.() ?? predefinedRanges.filter((range: RangeType) => range.label === defaultRange)[0]?.value;
}

export const getDateRangeByLabel = (label: string): Date[] | null => {
    const build = RANGE_BUILDERS[label];
    if (build) return build();
    const match = predefinedRanges.find((range) => range.label === label);
    const value = match?.value;
    if (!value || typeof value === 'function' || !Array.isArray(value)) return null;
    return [value[0], value[1]];
}

const sameCalendarDay = (a: Date | moment.Moment, b: Date | moment.Moment) =>
    moment(a).format("YYYY-MM-DD") === moment(b).format("YYYY-MM-DD");

/** Which predefined range label matches the current picker value (calendar days), if any. */
export function matchDateRangePreset(dateRange: any): DateRanges | null {
    if (!dateRange?.[0] || !dateRange?.[1]) return null;
    for (const label of Object.keys(RANGE_BUILDERS) as DateRanges[]) {
        const built = RANGE_BUILDERS[label]();
        if (sameCalendarDay(dateRange[0], built[0]) && sameCalendarDay(dateRange[1], built[1])) {
            return label;
        }
    }
    return null;
}

/**
 * Prior period of equal shape for a matched preset.
 * Open-ended "to now" windows (Today, Last N Days, MTD, YTD, …) use the same clock time on the prior end.
 */
export function getPresetCompareRange(label: DateRanges, now: moment.Moment = moment()): [Date, Date] | null {
    switch (label) {
        case "Today":
            return [
                now.clone().subtract(1, "day").startOf("day").toDate(),
                now.clone().subtract(1, "day").toDate(),
            ];
        case "Yesterday":
            return RangeToTimes("2 Days Ago") as [Date, Date];
        case "2 Days Ago":
            return [
                now.clone().subtract(3, "day").startOf("day").toDate(),
                now.clone().subtract(3, "day").endOf("day").toDate(),
            ];
        case "Last 7 Days":
        case "Last 30 Days":
        case "Last 60 Days":
        case "7 to 45 Days": {
            const cur = RangeToTimes(label) as [Date, Date];
            const ms = moment(cur[1]).diff(moment(cur[0]));
            const compareEnd = moment(cur[0]).subtract(1, "second");
            return [compareEnd.clone().subtract(ms, "milliseconds").toDate(), compareEnd.toDate()];
        }
        case "Month to Date": {
            const day = Math.min(now.date(), now.clone().subtract(1, "month").daysInMonth());
            return [
                now.clone().subtract(1, "month").startOf("month").toDate(),
                now.clone().subtract(1, "month").date(day).hour(now.hour()).minute(now.minute()).second(now.second()).millisecond(0).toDate(),
            ];
        }
        case "Month to Yesterday": {
            const y = now.clone().subtract(1, "day");
            const day = Math.min(y.date(), y.clone().subtract(1, "month").daysInMonth());
            return [
                y.clone().subtract(1, "month").startOf("month").toDate(),
                y.clone().subtract(1, "month").date(day).endOf("day").toDate(),
            ];
        }
        case "Last Month":
            return [
                now.clone().subtract(2, "month").startOf("month").toDate(),
                now.clone().subtract(2, "month").endOf("month").toDate(),
            ];
        case "Last 6 Month":
            return [
                now.clone().subtract(12, "month").startOf("month").toDate(),
                now.clone().subtract(7, "month").endOf("month").toDate(),
            ];
        case "Last 12 Month":
            return [
                now.clone().subtract(24, "month").startOf("month").toDate(),
                now.clone().subtract(13, "month").endOf("month").toDate(),
            ];
        case "Last 24 Month":
            return [
                now.clone().subtract(48, "month").startOf("month").toDate(),
                now.clone().subtract(25, "month").endOf("month").toDate(),
            ];
        case "Year to Date": {
            const lastYear = now.clone().subtract(1, "year");
            return [
                lastYear.clone().startOf("year").toDate(),
                lastYear.clone().hour(now.hour()).minute(now.minute()).second(now.second()).millisecond(0).toDate(),
            ];
        }
        default:
            return null;
    }
}

/** Compare window for the current picker value when it matches a predefined preset; otherwise null. */
export function getCompareRangeForDateRange(dateRange: any): [Date, Date] | null {
    const label = matchDateRangePreset(dateRange);
    if (!label) return null;
    return getPresetCompareRange(label);
}
